import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { fetchUIPreferences, updateUIPreferences } from '../api/uiPreferences';
import { useAuth } from '../hooks/useAuth';
import { isAnonDemoOnly } from '../utils/authStorage';

export const BACKGROUND_PRESETS = ['void', 'carbon', 'neutral', 'warm', 'soft'];
const DEFAULT_PRESET = 'void';
const STORAGE_KEY = 'debono.ui.background_preset';
const MATCH_1440P_PROPORTIONS_STORAGE_KEY = 'debono.ui.match_1440p_proportions';
const MATCH_1440P_REFERENCE_WIDTH = 1440;

export const UI_DENSITIES = ['compact', 'standard', 'spacious'];
const DEFAULT_DENSITY = 'standard';
const DENSITY_STORAGE_KEY = 'debono.ui.density';

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'debono.ui.sidebar_collapsed';

const UISettingsContext = createContext({
  backgroundPreset: DEFAULT_PRESET,
  setBackgroundPreset: () => {},
  match1440pProportions: false,
  setMatch1440pProportions: () => {},
  match1440pScale: 1,
  isMatch1440pScaleActive: false,
  uiDensity: DEFAULT_DENSITY,
  setUIDensity: () => {},
  sidebarCollapsed: false,
  setSidebarCollapsed: () => {},
  toggleSidebarCollapsed: () => {},
  loading: false,
});

function isValidPreset(value) {
  return BACKGROUND_PRESETS.includes(value);
}

function isValidDensity(value) {
  return UI_DENSITIES.includes(value);
}

function applyPreset(preset) {
  const value = isValidPreset(preset) ? preset : DEFAULT_PRESET;
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.bgPreset = value;
  }
}

function applyDensity(density) {
  const value = isValidDensity(density) ? density : DEFAULT_DENSITY;
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.uiDensity = value;
  }
}

function applySidebarCollapsed(collapsed) {
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.sidebarCollapsed = collapsed ? 'true' : 'false';
  }
}

function readMatch1440pPreference() {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(MATCH_1440P_PROPORTIONS_STORAGE_KEY) === 'true';
}

function computeMatch1440pScale() {
  if (typeof window === 'undefined') return 1;
  const innerWidth = Number(window.innerWidth) || MATCH_1440P_REFERENCE_WIDTH;
  if (innerWidth <= 0) return 1;
  return innerWidth / MATCH_1440P_REFERENCE_WIDTH;
}

export function UISettingsProvider({ children }) {
  const { user } = useAuth();
  const [backgroundPreset, setBackgroundPresetState] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_PRESET;
    const cached = window.localStorage.getItem(STORAGE_KEY);
    return isValidPreset(cached) ? cached : DEFAULT_PRESET;
  });
  const [match1440pProportions, setMatch1440pProportionsState] = useState(readMatch1440pPreference);
  const [match1440pScale, setMatch1440pScale] = useState(computeMatch1440pScale);
  const [uiDensity, setUIDensityState] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_DENSITY;
    const cached = window.localStorage.getItem(DENSITY_STORAGE_KEY);
    return isValidDensity(cached) ? cached : DEFAULT_DENSITY;
  });
  const [sidebarCollapsed, setSidebarCollapsedState] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
  });
  const [loading, setLoading] = useState(false);
  const saveTimerRef = useRef(null);

  useEffect(() => {
    applyPreset(user ? backgroundPreset : DEFAULT_PRESET);
  }, [backgroundPreset, user]);

  useEffect(() => {
    applyDensity(uiDensity);
  }, [uiDensity]);

  // Anon-demo (/play) tab: pin the sidebar collapsed without persisting. We drive
  // ONLY the <html data-sidebar-collapsed> attribute (the non-persisting seam),
  // never the localStorage-backed setter — so the real user's stored preference
  // is untouched and reverts cleanly when they leave the demo. Re-evaluated when
  // `user` changes (sign-in releases isolation → flag false → real pref returns).
  const forceSidebarCollapsed = isAnonDemoOnly();
  const effectiveSidebarCollapsed = forceSidebarCollapsed || sidebarCollapsed;

  // useLayoutEffect so the collapsed attribute is on <html> before first paint
  // (no expand→collapse flash on reload when the user left the sidebar closed).
  useLayoutEffect(() => {
    applySidebarCollapsed(effectiveSidebarCollapsed);
    // Mark the anon /play demo so demo-only chrome (e.g. compact carousel sizing)
    // can scope to it without touching a real user's settings. Tracks the demo
    // flag itself, NOT a logged-in user's manual sidebar collapse.
    if (typeof document !== 'undefined') {
      document.documentElement.dataset.anonDemo = forceSidebarCollapsed ? 'true' : 'false';
    }
  }, [effectiveSidebarCollapsed, forceSidebarCollapsed]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    fetchUIPreferences()
      .then((prefs) => {
        if (cancelled) return;
        if (isValidPreset(prefs?.background_preset)) {
          setBackgroundPresetState(prefs.background_preset);
          window.localStorage.setItem(STORAGE_KEY, prefs.background_preset);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => {
      setMatch1440pScale(computeMatch1440pScale());
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const setBackgroundPreset = useCallback(
    (next) => {
      if (!isValidPreset(next)) return;
      setBackgroundPresetState(next);
      window.localStorage.setItem(STORAGE_KEY, next);
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
      if (!user) return;
      saveTimerRef.current = window.setTimeout(() => {
        updateUIPreferences({ background_preset: next }).catch((err) => {
          console.warn('Failed to save UI background preference:', err?.message || err);
        });
      }, 400);
    },
    [user]
  );

  const setUIDensity = useCallback((next) => {
    if (!isValidDensity(next)) return;
    setUIDensityState(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(DENSITY_STORAGE_KEY, next);
    }
  }, []);

  const setSidebarCollapsed = useCallback((next) => {
    const normalized = !!next;
    setSidebarCollapsedState(normalized);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(
        SIDEBAR_COLLAPSED_STORAGE_KEY,
        normalized ? 'true' : 'false'
      );
    }
  }, []);

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsedState((prev) => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(
          SIDEBAR_COLLAPSED_STORAGE_KEY,
          next ? 'true' : 'false'
        );
      }
      return next;
    });
  }, []);

  const setMatch1440pProportions = useCallback((next) => {
    const normalized = !!next;
    setMatch1440pProportionsState(normalized);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(
        MATCH_1440P_PROPORTIONS_STORAGE_KEY,
        normalized ? 'true' : 'false'
      );
    }
  }, []);

  const isMatch1440pScaleActive =
    match1440pProportions && Math.abs(match1440pScale - 1) > 0.005;

  const value = useMemo(
    () => ({
      backgroundPreset,
      setBackgroundPreset,
      match1440pProportions,
      setMatch1440pProportions,
      match1440pScale,
      isMatch1440pScaleActive,
      uiDensity,
      setUIDensity,
      sidebarCollapsed,
      forceSidebarCollapsed,
      setSidebarCollapsed,
      toggleSidebarCollapsed,
      loading,
    }),
    [
      backgroundPreset,
      setBackgroundPreset,
      match1440pProportions,
      setMatch1440pProportions,
      match1440pScale,
      isMatch1440pScaleActive,
      uiDensity,
      setUIDensity,
      sidebarCollapsed,
      forceSidebarCollapsed,
      setSidebarCollapsed,
      toggleSidebarCollapsed,
      loading,
    ]
  );

  return <UISettingsContext.Provider value={value}>{children}</UISettingsContext.Provider>;
}

export function useUISettings() {
  return useContext(UISettingsContext);
}
